import { h } from "preact";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "preact/hooks";
import TimerContext from "../../features/utils/TimerContext";
import { Match } from "../helpers/findSearchMatches";
import getRoundedNum from "../helpers/getRoundedNum";

import { paragraphsToTranscriptHTML } from "../helpers/paragraphsToTranscriptHTML";
import { Paragraph } from "../types/transcriptApiTypes";

import css from "./Transcript.module.scss";

interface FastTranscriptBodyProps {
  paragraphs: Paragraph[];
  searchResults: Match[];
  onWordClick: (milliseconds: number) => void;
}

export function FastTranscriptBody({
  paragraphs,
  searchResults,
  onWordClick,
}: FastTranscriptBodyProps) {
  const { activeTimeInMilliseconds: activeMillisecond } =
    useContext(TimerContext);

  const bodyRef = useRef<HTMLDivElement>(null);
  const needsSearchCleanUpRef = useRef(false);

  const sanitizedHTML = useMemo(
    // DO NOT add any arguments here that would change every render.
    // We should only recompute this if language changes.
    // To modify with data, use effects below to alter the DOM for active
    // highlight, search result styling, etc.
    () => paragraphsToTranscriptHTML(paragraphs, css.paragraph),
    [paragraphs]
  );

  const handleBubbledWordClick = useCallback(
    (event: MouseEvent) => {
      let element = event.target as HTMLElement | null;

      while (element !== bodyRef.current && element?.parentElement) {
        if (
          element instanceof HTMLSpanElement &&
          element.hasAttribute("data-mil")
        ) {
          onWordClick(Number(element.dataset.mil) / 1000); // onWordClick takes seconds
          break;
        } else {
          element = element?.parentElement;
        }
      }
    },
    [onWordClick]
  );

  // Scrolls highlighted text into view.
  useEffect(() => {
    const currentElement = bodyRef.current?.querySelector(
      `span[data-rounded-mil="${activeMillisecond}"]`
    );

    currentElement?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeMillisecond]);

  // Updates search results (and cleans up from prior setup)
  // TODO: This would benefit from some refactoring extracting out some helpers.
  useEffect(() => {
    if (needsSearchCleanUpRef.current) {
      // clean up last set of mark elements
      bodyRef.current
        ?.querySelectorAll("mark")
        .forEach((element) => element.remove());

      bodyRef.current
        ?.querySelectorAll('span[aria-hidden="true"]')
        .forEach((element) => {
          element.removeAttribute("aria-hidden");
        });

      needsSearchCleanUpRef.current = false;
    }

    if (searchResults.length > 0) {
      searchResults.forEach(({ paragraphIndex, words }) => {
        const firstWord = words[0];

        // there should always be a word, but just in case.
        if (!firstWord || firstWord.text.length === 0) return;

        const firstElement = bodyRef.current?.querySelector(
          `p[data-index="${paragraphIndex}"] span[data-index="${firstWord.index}"]`
        );

        if (firstElement) {
          const resultElement = document.createElement("mark");

          words.forEach((word, index) => {
            // TODO it may be faster to build up an HTML string to append to the
            // Mark element in one go; worth profiling to see if we can improve
            // search speed on generic searches. Still works ok though.
            const span = document.createElement("span");

            span.dataset.index = word.index.toString();
            span.dataset.mil = word.millisecond.toString();
            span.dataset.roundedMil = getRoundedNum(
              word.millisecond
            ).toString();

            if (word.italic) {
              const i = document.createElement("i");
              i.innerText = word.text; // DO NOT use innerHTML (XSS)
              span.appendChild(i);
            } else {
              span.innerText = word.text; // DO NOT use innerHTML (XSS)
            }

            resultElement.appendChild(span);

            if (index !== words.length - 1) {
              resultElement.appendChild(document.createTextNode(" "));
            }

            // We then hide the current spans (but leave them in the DOM to
            // preserve our nth-of-type selector).
            const element = bodyRef.current?.querySelector(
              `p[data-index="${paragraphIndex}"] span[data-index="${word.index}"]`
            );

            if (element) {
              element.ariaHidden = "true";
            }
          });

          firstElement.insertAdjacentElement("beforebegin", resultElement);
          firstElement.insertAdjacentText("beforebegin", " ");
        }
      });

      needsSearchCleanUpRef.current = true;
    }
  }, [paragraphs, searchResults]);

  // TODO this could possibly benefit from a binary search or we could start
  // searching from a prior index. It probably doesn't matter; most paragraphs
  // are in the order of ~288.
  const currentParagraphIndex =
    paragraphs.findIndex((paragraph, index) => {
      const nextTimestamp = paragraphs[index + 1]?.millisecond || Infinity;
      return (
        paragraph.millisecond <= activeMillisecond &&
        activeMillisecond < nextTimestamp
      );
    }) ?? 0;

  const currentParagraph = paragraphs[currentParagraphIndex];

  // Finds the **first** word for the current timestamp. This is used to compute
  // prior words.
  // TODO this could possibly benefit from a binary search or we could start
  // searching from a last-known index to improve each frame's search. It may
  // matter in the rare edge case where we have a massive transcript in one
  // paragraph.
  const currentWordIndex =
    currentParagraph?.words.findIndex((word, index) => {
      const nextTimestamp =
        currentParagraph?.words[index + 1]?.millisecond || Infinity;

      return (
        word.millisecond <= activeMillisecond &&
        activeMillisecond <= nextTimestamp
      );
    }) ?? 0;

  return (
    <div
      id="transcript-body"
      class="transcript-body"
      ref={bodyRef}
      onClick={handleBubbledWordClick}
    >
      {/* 
        Note that we use the ID selector to beat specificity of the previous 
        words as we pad out the prior paragraph by 1 to cover for word overlap.

        Search highlight should always trump active highlight specificity.
       */}
      <style>{`
        .transcript-body p:nth-of-type(-n + ${currentParagraphIndex}) {
          color: #656568;
        }

        .transcript-body p:nth-of-type(${
          currentParagraphIndex + 1
        }) > span:nth-of-type(-n + ${currentWordIndex + 1}) {
          color: #656568;
        }

        #transcript-body span[aria-hidden] {
          display: none;
        }

        #transcript-body p > span[data-rounded-mil="${activeMillisecond}"] {
          background: #201f23;
          color: #fff;
        }

        #transcript-body mark {
          background: #007eb5;
        }
        
        #transcript-body mark span {
          color: #fff;
        }
      `}</style>
      {/*  
        Preact does not rerender if the __html strings are identical, so we
        don't need to wrap the `dangerouslySetInnerHTML` in our own PureComponent.
        https://github.com/preactjs/preact/blob/1dc4f56916a9b24e4796d839122757cbd7fc14db/src/diff/index.js#L400
      */}
      <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
      {paragraphs.length === 0 && <i>No transcript available.</i>}
    </div>
  );
}
